import type { StoredMutation } from '../platform/webStorage'
import { isRetiredPageText } from './outboxPolicy'
import { syncLog } from './syncLog'

// One pass of the outbox: the items due, sorted by where they go, and the
// sync log's account of what was sent.

export interface OutboxBatch {
  tree: Array<{ id: string; payload: any }>
  documents: Array<{ id: string; targetId: string; action: string; payload: any; hlc: string }>
  snapshots: Array<{ id: string; targetId: string; payload: any }>
  settings: Array<{ id: string; payload: any }>
  /** Items of no known kind, or whose payload can't be read: dropped unsent. */
  dropped: string[]
}

/** Sorts the due items by kind, each with its payload parsed. */
export function groupOutboxItems(items: StoredMutation[]): OutboxBatch {
  const batch: OutboxBatch = { tree: [], documents: [], snapshots: [], settings: [], dropped: [] }
  for (const mut of items) {
    try {
      const parsed = typeof mut.payload === 'string' ? JSON.parse(mut.payload) : mut.payload

      if (mut.type === 'project') {
        batch.tree.push({ id: mut.id, payload: parsed })
      } else if (mut.type === 'settings') {
        batch.settings.push({ id: mut.id, payload: parsed })
      } else if (mut.type === 'document') {
        batch.documents.push({
          id: mut.id,
          targetId: mut.targetId,
          action: mut.action,
          payload: parsed,
          hlc: mut.hlc,
        })
      } else if (mut.type === 'snapshot') {
        batch.snapshots.push({
          id: mut.id,
          targetId: mut.targetId,
          payload: parsed,
        })
      } else {
        batch.dropped.push(mut.id)
      }
    } catch {
      batch.dropped.push(mut.id)
    }
  }
  return batch
}

/** How an outbox item reads in the sync log. */
export function describeMutation(item: StoredMutation): { label: string; targetType: 'page' | 'tree' | 'setting' | 'snapshot'; targetId: string } {
  if (item.type === 'project') return { label: 'Folder or page change', targetType: 'tree', targetId: item.targetId }
  if (item.type === 'settings') return { label: 'Setting', targetType: 'setting', targetId: item.targetId }
  if (item.type === 'snapshot') {
    let documentId = item.targetId
    try { documentId = JSON.parse(item.payload).documentId || documentId } catch {}
    return { label: 'Saved version', targetType: 'snapshot', targetId: documentId }
  }
  if (item.action === 'delete') return { label: 'Permanent deletion', targetType: 'page', targetId: item.targetId }
  return { label: 'Page text from an older version (dropped)', targetType: 'page', targetId: item.targetId }
}

/** Logs one line per kind of change a pass sent. */
export function logSent(attempted: StoredMutation[], sentIds: string[]): void {
  const sent = new Set(sentIds)
  const groups = new Map<string, { message: string; targetType: 'page' | 'tree' | 'setting' | 'snapshot'; ids: string[] }>()
  for (const item of attempted) {
    // Page text from older builds is dropped unsent, not sent.
    if (!sent.has(item.id) || isRetiredPageText(item)) continue
    const what = describeMutation(item)
    const message = item.type === 'project' ? 'Sent folder and page changes'
      : item.type === 'settings' ? 'Sent settings'
      : item.type === 'snapshot' ? 'Sent saved versions'
      : 'Deleted pages permanently from the cloud'
    const group = groups.get(message) ?? { message, targetType: what.targetType, ids: [] }
    group.ids.push(what.targetId)
    groups.set(message, group)
  }
  for (const group of groups.values()) {
    syncLog.record({ direction: 'out', kind: 'outbox-sent', message: group.message, targetType: group.targetType, targetIds: group.ids, items: group.ids.length })
  }
}
